import { Link } from 'react-router-dom';
import type { ListingCard } from '@solote/contracts';
import { Badge, Skeleton, cx } from '@/design-system';
import { formatArea, formatCents } from '@/core/format/br';

/**
 * CARD DO ANÚNCIO
 *
 * Hierarquia deliberada: preço primeiro, área e valor por m² logo abaixo,
 * título depois. Quem procura lote compara preço e metragem antes de ler
 * qualquer coisa — inverter essa ordem obriga o olho a trabalhar à toa.
 *
 * O que aparece aqui já vem resolvido do servidor: se a privacidade é
 * "somente cidade", `neighborhood` chega nulo e o card simplesmente não
 * mostra bairro. Nada é escondido no cliente.
 */

function PinIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11Z" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  );
}

function AreaIcon() {
  return (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M4 4h16v16H4z" strokeLinejoin="round" />
      <path d="M4 9h4M16 15h4M9 4v4M15 16v4" strokeLinecap="round" />
    </svg>
  );
}

/** Qual selo vale a pena ocupar espaço na foto, em ordem de importância. */
const BADGE_PRIORITY = ['destaque', 'novo', 'matricula'];

export function ListingCardView({ listing }: { listing: ListingCard }) {
  const place = [listing.location.neighborhood, listing.location.city].filter(Boolean).join(', ');
  const state = listing.location.state;

  const visibleBadges = [...listing.badges]
    .sort((a, b) => {
      const rank = (key: string) => {
        const index = BADGE_PRIORITY.indexOf(key);
        return index === -1 ? BADGE_PRIORITY.length : index;
      };
      return rank(a.key) - rank(b.key);
    })
    .slice(0, 2);

  return (
    <article
      className={cx(
        'group relative flex flex-col overflow-hidden rounded-(--radius-card) border bg-surface',
        'shadow-(--shadow-card) transition-shadow duration-200 hover:shadow-(--shadow-lift)',
        listing.featured ? 'border-primary/45' : 'border-line',
      )}
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-surface-muted">
        {listing.coverImageUrl ? (
          <img
            src={listing.coverImageUrl}
            alt=""
            loading="lazy"
            decoding="async"
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-ink-faint" aria-hidden="true">
            <AreaIcon />
          </div>
        )}

        {/*
          No máximo dois selos. Com três eles quebram em duas linhas e cobrem
          metade da foto — o que mais atrapalha do que informa. A ordem de
          prioridade decide quais sobrevivem.
        */}
        {visibleBadges.length > 0 ? (
          <div className="pointer-events-none absolute inset-x-3 top-3 flex flex-wrap gap-1.5">
            {visibleBadges.map((badge) => (
              <Badge key={badge.key} tone={badge.key === 'destaque' ? 'solid' : (badge.tone as 'neutral')}>
                {badge.label}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-2 p-4">
        <p className="tnum font-display text-xl font-bold leading-none text-ink">{formatCents(listing.priceCents)}</p>

        {/*
          Altura reservada para duas linhas. Sem isso, um valor por m² longo
          quebra a linha só nesse card e os títulos da fileira inteira ficam
          em alturas diferentes.
        */}
        <div className="tnum flex min-h-10 flex-wrap items-start gap-x-3 gap-y-0.5 text-sm text-ink-soft">
          <span className="inline-flex items-center gap-1">
            <AreaIcon />
            {formatArea(listing.totalAreaM2)}
          </span>
          {listing.pricePerSquareMeterCents ? (
            <span className="text-ink-faint">{formatCents(listing.pricePerSquareMeterCents)}/m²</span>
          ) : null}
        </div>

        <h3 className="line-clamp-2 min-h-11 text-[0.9375rem] font-semibold leading-snug text-ink">
          {/* O link cobre o card inteiro, mas o nome acessível continua sendo
              o título — evita "link" sem rótulo no leitor de tela. */}
          <Link to={`/anuncio/${listing.slug}`} className="no-underline after:absolute after:inset-0 after:content-['']">
            {listing.title}
          </Link>
        </h3>

        <p className="mt-auto flex items-center gap-1 pt-1 text-sm text-ink-soft">
          <PinIcon />
          <span className="truncate">
            {place}
            {state ? ` · ${state}` : ''}
          </span>
        </p>

        <p className="tnum text-[0.6875rem] font-medium uppercase tracking-wide text-ink-faint">{listing.code}</p>
      </div>
    </article>
  );
}

/** Grade responsiva padrão do catálogo. */
export function ListingGrid({ listings }: { listings: ListingCard[] }) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {listings.map((listing) => (
        <ListingCardView key={listing.id} listing={listing} />
      ))}
    </div>
  );
}

export function ListingGridSkeleton({ count = 8 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {Array.from({ length: count }).map((_, index) => (
        <div key={index} className="overflow-hidden rounded-(--radius-card) border border-line bg-surface">
          <Skeleton className="aspect-[4/3] w-full rounded-none" />
          <div className="flex flex-col gap-2 p-4">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-2/3" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-1/3" />
          </div>
        </div>
      ))}
    </div>
  );
}
